/**
 * File: routeAccessUtils.ts
 * Path: apps/admin/src/modules/auth/utils/routeAccessUtils.ts
 * Description: The admin route guard's decision, as a pure function.
 *
 * Extracted so the rules — including the deactivated-account case, which the
 * route previously ignored entirely — can be asserted directly rather than
 * only through a rendered router.
 */

import type { Permission } from "@idk/types";

export type RouteAccessDecision =
  | { kind: "pending" }
  | { kind: "allow" }
  | { kind: "redirect"; to: string };

export interface RouteAccessInput {
  /** Whether Firebase Auth has resolved the session yet. */
  isLoading: boolean;
  /** True once a signed-in user exists. */
  isAuthenticated: boolean;
  /** The Firestore profile's `disabled` flag, when the profile has loaded. */
  isDisabled?: boolean;
  /** No staff role. */
  isViewer: boolean;
  /** Whether the signed-in role holds a permission. */
  can: (permission: Permission) => boolean;
  /** The permission the route needs, if any. */
  requires?: Permission;
}

export const resolveRouteAccess = ({
  isLoading,
  isAuthenticated,
  isDisabled,
  isViewer,
  can,
  requires,
}: RouteAccessInput): RouteAccessDecision => {
  // 1. Wait for the session before deciding anything.
  if (isLoading) return { kind: "pending" };

  // 2. Authentication.
  if (!isAuthenticated) return { kind: "redirect", to: "/login" };

  // 3. Deactivated accounts. Revoking refresh tokens ends the session on the
  //    next refresh, but that can be up to an hour away — the admin app must
  //    not stay usable in the meantime.
  if (isDisabled) {
    return { kind: "redirect", to: "/login?reason=deactivated" };
  }

  // 4. Viewers are denied the admin app outright.
  if (isViewer) return { kind: "redirect", to: "/login?reason=forbidden" };

  // 5. Permission gating (packages/types ROLE_PERMISSIONS).
  if (requires && !can(requires)) {
    return { kind: "redirect", to: "/dashboard" };
  }

  return { kind: "allow" };
};
